import { Component, OnInit, OnDestroy, inject, SecurityContext, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { SafeStorageService } from '@core/services/safe-storage.service';
import {
  ToastNotificationService,
  ToastNotification,
} from '../../../core/services/toast-notification.service';

// Production Desktop Notification Container Component
// Displays desktop notifications and permission prompts for the Scan Ninja application

@Component({
  selector: 'app-toast-container',
  templateUrl: './toast-container.component.html',
  styleUrl: './toast-container.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ToastContainerComponent implements OnInit, OnDestroy {
  private toastService = inject(ToastNotificationService);
  private sanitizer = inject(DomSanitizer);
  private safeStorage = inject(SafeStorageService);

  notifications: ToastNotification[] = [];
  showPermissionPrompt = false;
  isRequestingPermission = false;
  private subscription: Subscription = new Subscription();
  private readonly PERM_SNOOZE_KEY = 'sn_toast_perm_snooze_until';
  private readonly PERM_SNOOZE_MS = 24 * 60 * 60 * 1000;

  ngOnInit(): void {
    this.subscription.add(
      this.toastService.getNotifications().subscribe((notifications) => {
        this.notifications = notifications;
      }),
    );

    // React to permission changes
    this.subscription.add(
      this.toastService.getPermission$().subscribe(() => {
        this.checkNotificationPermission();
      }),
    );

    // Initial state
    this.checkNotificationPermission();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  /**
   * Check notification permission and show prompt if needed
   */
  private checkNotificationPermission(): void {
    if (this.toastService.isDesktopNotificationSupported()) {
      const permission = this.toastService.getNotificationPermission();
      this.showPermissionPrompt = permission === 'default' && !this.isSnoozed();
    } else {
      this.showPermissionPrompt = false; // Not supported, never prompt
    }
  }

  private isSnoozed(): boolean {
    const raw = this.safeStorage.getItem(this.PERM_SNOOZE_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    return Number.isFinite(until) && Date.now() < until;
  }

  private snoozePermissionPrompt(): void {
    const until = Date.now() + this.PERM_SNOOZE_MS;
    this.safeStorage.setItem(this.PERM_SNOOZE_KEY, String(until));
  }

  /**
   * Request notification permission
   */
  async requestNotificationPermission(): Promise<void> {
    if (this.isRequestingPermission) {
      return; // Prevent double-clicks
    }

    this.isRequestingPermission = true;
    console.warn('Requesting notification permission...');

    try {
      const granted = await this.toastService.requestNotificationPermission();
      if (!granted) {
        // Quiet UX: snooze future prompts for a day when user declines/dismisses
        this.snoozePermissionPrompt();
      }
      this.showPermissionPrompt = false; // Hide either way
    } catch (error) {
      // Quiet-fail: do not surface an error toast
      console.warn('Notification permission request failed:', error);
      this.snoozePermissionPrompt();
      this.showPermissionPrompt = false;
    } finally {
      this.isRequestingPermission = false;
    }
  }

  /**
   * Dismiss permission prompt
   */
  dismissPermissionPrompt(): void {
    this.snoozePermissionPrompt();
    this.showPermissionPrompt = false;
  }

  /**
   * Track by function for ngFor performance
   */
  trackByNotificationId(
    index: number,
    notification: ToastNotification,
  ): string {
    return notification.id;
  }

  /**
   * Remove a notification
   */
  removeNotification(id: string): void {
    this.toastService.removeNotification(id);
  }

  /**
   * Execute notification action and remove notification
   */
  executeAction(notification: ToastNotification): void {
    if (notification.action) {
      notification.action.callback();
      this.removeNotification(notification.id);
    }
  }

  /**
   * Get CSS classes for notification type
   */
  getNotificationClasses(type: string): string {
    const baseClasses =
      'toast-notification animate__animated animate__slideInRight';
    switch (type) {
      case 'success':
        return `${baseClasses} toast-success`;
      case 'error':
        return `${baseClasses} toast-error`;
      case 'warning':
        return `${baseClasses} toast-warning`;
      case 'info':
        return `${baseClasses} toast-info`;
      default:
        return `${baseClasses} toast-info`;
    }
  }

  /**
   * Get icon for notification type
   */
  getNotificationIcon(type: string): string {
    switch (type) {
      case 'success':
        return 'fas fa-check-circle';
      case 'error':
        return 'fas fa-exclamation-triangle';
      case 'warning':
        return 'fas fa-exclamation-circle';
      case 'info':
        return 'fas fa-info-circle';
      default:
        return 'fas fa-info-circle';
    }
  }

  /**
   * Format timestamp for display
   */
  formatTime(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return timestamp.toLocaleDateString();
    }
  }

  /**
   * Sanitize HTML content for safe display
   * Allows basic formatting like <br>, <strong>, <em> but strips dangerous elements
   */
  getSanitizedMessage(message: string): SafeHtml {
    if (!message) return '' as unknown as SafeHtml;

    // Robust sanitization using a DOMParser and strict whitelist
    const allowedTags = new Set(['BR', 'STRONG', 'EM', 'B', 'I']);

    const parser = new DOMParser();
    const doc = parser.parseFromString(message, 'text/html');

    const cleanNode = (node: Node) => {
      // Process element nodes
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toUpperCase();

        // If tag not allowed, replace with its text content
        if (!allowedTags.has(tag)) {
          const text = doc.createTextNode(el.textContent || '');
          el.replaceWith(text);
          return; // Node replaced, stop descending
        }

        // For allowed tags, remove all attributes to prevent injection
        Array.from(el.attributes).forEach((attr) => {
          el.removeAttribute(attr.name);
        });

        // Recursively clean child nodes
        Array.from(el.childNodes).forEach(cleanNode);
      } else if (node.nodeType === Node.TEXT_NODE) {
        // Leave text nodes as-is
        return;
      } else {
        // Remove comments or other node types
        node.parentNode?.removeChild(node);
      }
    };

    Array.from(doc.body.childNodes).forEach(cleanNode);

    const sanitizedHtml = doc.body.innerHTML.trim();
    const safe = this.sanitizer.sanitize(SecurityContext.HTML, sanitizedHtml) ?? '';
    return safe as unknown as SafeHtml;
  }

  /**
   * Sanitize plain text content (for titles, labels, etc.)
   * Strips all HTML and returns plain text
   */
  getSanitizedText(text: string): string {
    if (!text) return '';

    // Strip all HTML tags and decode HTML entities
    const div = document.createElement('div');
    div.innerHTML = text;
    return div.textContent || div.innerText || '';
  }
}
